import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Eye, Zap, Globe, Factory, Lightbulb, Rocket, Award, BadgeCheck, Scale, HeartPulse } from 'lucide-react';
import { useScrollReveal } from '../hooks/useAnimations';
import { useQuote } from '../context/QuoteContext';

const Reveal: React.FC<{ children: React.ReactNode; className?: string; delay?: number }> = ({ children, className = '', delay = 0 }) => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <div ref={ref} className={`transition-all duration-[800ms] ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const About: React.FC = () => {
  const { openQuote } = useQuote();
  return (
    <main className="bg-white overflow-hidden">

      {/* ═══ HERO ═══ */}
      <section className="relative pt-28 pb-14 lg:pt-36 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-silver-50 via-brand-50/30 to-white" />
        <div className="absolute inset-0 dot-grid opacity-[0.3]" />
        <div className="absolute top-20 right-[15%] w-[500px] h-[500px] bg-brand-200/20 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-brand-100/15 rounded-full blur-[100px] pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="animate-fade-in">
            <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 bg-brand-50 px-4 py-1.5 rounded-full mb-5">About Us</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-silver-950 tracking-tight leading-[1.1] animate-slide-up">
            The Power Behind{' '}
            <span className="text-gradient">the Purity</span>
          </h1>
          <p className="mt-7 text-lg text-silver-500 max-w-2xl mx-auto leading-relaxed animate-slide-up-delayed">
            Arohix Water is a product of Nathkrupa Energy — built on decades of expertise in industrial innovation, now channeled into delivering India's finest packaged drinking water.
          </p>
        </div>
      </section>

      {/* ═══ MISSION ═══ */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            <Reveal>
              <div>
                <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 bg-brand-50 px-4 py-1.5 rounded-full mb-5">Our Story</span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-silver-950 tracking-tight">
                  Born from Nathkrupa Energy
                </h2>
                <div className="mt-8 space-y-5 text-silver-600 leading-relaxed text-[15px]">
                  <p>
                    Nathkrupa Energy, a trusted name in Maharashtra's industrial landscape, identified a critical gap in India's packaged drinking water industry — the absence of truly transparent, science-first hydration solutions.
                  </p>
                  <p>
                    With decades of operational expertise, Nathkrupa Energy channeled its resources into building a state-of-the-art water purification facility — one that goes beyond BIS and FSSAI compliance to set entirely new standards.
                  </p>
                  <p>
                    Arohix Water is the culmination of that vision: premium drinking water processed through a 7-stage multi-barrier purification system, bottled in a 100% automated plant with zero human contact.
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={200}>
              <div className="relative">
                <div className="bg-gradient-to-br from-silver-50 to-brand-50/30 border border-silver-100/80 rounded-3xl p-10 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-48 h-48 bg-brand-200/15 rounded-full blur-[60px]" />

                  <div className="relative space-y-0">
                    {[
                      { year: 'Foundation', text: 'Nathkrupa Energy established as a leading industrial enterprise in Maharashtra.', icon: <Factory className="w-5 h-5 text-brand-600" /> },
                      { year: 'Innovation', text: 'Identified the gap in transparent, quality-first packaged water in India.', icon: <Lightbulb className="w-5 h-5 text-amber-500" /> },
                      { year: 'Arohix Launch', text: '7-stage purification plant commissioned with 100% automation — zero human touch.', icon: <Rocket className="w-5 h-5 text-violet-500" /> },
                    ].map((item, i) => (
                      <div key={i} className="flex gap-5 group">
                        <div className="flex flex-col items-center">
                          <div className="w-10 h-10 rounded-xl bg-white border border-silver-200/60 shadow-sm flex items-center justify-center text-lg group-hover:scale-110 group-hover:shadow-md transition-all duration-300">
                            {item.icon}
                          </div>
                          {i < 2 && <div className="w-px flex-1 bg-gradient-to-b from-brand-200 to-silver-200 mt-2 mb-2" />}
                        </div>
                        <div className="pb-8 pt-1">
                          <div className="text-xs font-bold text-brand-600 uppercase tracking-wider">{item.year}</div>
                          <p className="mt-1.5 text-sm text-silver-600 leading-relaxed">{item.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ PLANT VIDEO ═══ */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-10">
              <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 bg-brand-50 px-4 py-1.5 rounded-full mb-4">Arohix Water</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-silver-950 tracking-tight">
                Pure Water, <span className="text-gradient">Every Drop</span>
              </h2>
              <p className="mt-4 text-silver-500 max-w-lg mx-auto">
                7-stage purified, mineral-enriched and sealed for freshness — Arohix Water, crafted for those who demand the best.
              </p>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-silver-100/80 group">
              <video
                autoPlay
                muted
                loop
                playsInline
                className="w-full aspect-video object-cover"
                src="/assets/videos/Aerohix-water-video.mp4"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-900/30 to-transparent pointer-events-none rounded-3xl" />
              <div className="absolute bottom-6 left-6 flex items-center gap-3">
                <div className="flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg">
                  <div className="w-5 h-5 rounded-full bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center">
                    <span className="text-[8px] font-black text-white">7</span>
                  </div>
                  <span className="text-[11px] font-bold text-brand-700 uppercase tracking-[0.12em]">7-Stage Purified</span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ CERTIFICATIONS ═══ */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-silver-50/50 to-white relative">
        <div className="absolute inset-0 dot-grid opacity-[0.2]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-16">
              <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 bg-brand-50 px-4 py-1.5 rounded-full mb-4">Compliance</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-silver-950 tracking-tight">
                Certified & <span className="text-gradient">Compliant</span>
              </h2>
              <p className="mt-5 text-silver-500 max-w-lg mx-auto text-lg">
                Every bottle of Arohix meets or exceeds national and international water quality benchmarks.
              </p>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: <Award className="w-7 h-7 text-white" />, name: 'ISO 9001:2015', desc: 'International quality management system certification.', color: 'from-blue-500 to-blue-600' },
              { icon: <BadgeCheck className="w-7 h-7 text-white" />, name: 'FSSAI Licensed', desc: 'Food Safety and Standards Authority of India certified.', color: 'from-emerald-500 to-emerald-600' },
              { icon: <Scale className="w-7 h-7 text-white" />, name: 'BIS Approved', desc: 'Bureau of Indian Standards IS 14543 compliant.', color: 'from-violet-500 to-violet-600' },
              { icon: <HeartPulse className="w-7 h-7 text-white" />, name: 'WHO Guidelines', desc: 'Meets World Health Organization water quality parameters.', color: 'from-sky-500 to-sky-600' },
            ].map((cert, i) => (
              <Reveal key={cert.name} delay={i * 100}>
                <div className="group bg-white border border-silver-100 rounded-2xl p-7 hover:shadow-card-hover hover:border-silver-200/80 hover:-translate-y-1 transition-all duration-500 text-center gradient-border h-full">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${cert.color} flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    {cert.icon}
                  </div>
                  <h3 className="text-sm font-bold text-silver-900 mb-2">{cert.name}</h3>
                  <p className="text-xs text-silver-500 leading-relaxed">{cert.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ THE HILLY EDGE ═══ */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            <Reveal>
              <div className="relative">
                <div className="bg-gradient-to-br from-brand-50 via-sky-50/50 to-white rounded-3xl p-6 border border-silver-100/80 overflow-hidden" style={{ height: '400px' }}>
                  <svg viewBox="0 0 500 340" className="w-full h-full" fill="none">
                    <defs>
                      <linearGradient id="skyGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#dbeafe" />
                        <stop offset="100%" stopColor="#eff6ff" />
                      </linearGradient>
                      <linearGradient id="waterGrad" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#93c5fd" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.15" />
                      </linearGradient>
                    </defs>
                    <rect width="500" height="340" fill="url(#skyGrad)" rx="16" />

                    {/* Clouds */}
                    <ellipse cx="100" cy="50" rx="50" ry="15" fill="white" opacity="0.5" />
                    <ellipse cx="350" cy="70" rx="40" ry="12" fill="white" opacity="0.4" />

                    {/* Back hills */}
                    <path d="M0 220 Q60 140 150 190 Q240 140 330 175 Q420 140 500 160 V340 H0Z" fill="#93c5fd" opacity="0.25" />
                    <path d="M0 250 Q80 170 180 220 Q280 170 380 210 Q450 190 500 200 V340 H0Z" fill="#60a5fa" opacity="0.25" />
                    <path d="M0 270 Q120 210 250 250 Q380 220 500 240 V340 H0Z" fill="#3b82f6" opacity="0.2" />

                    {/* Water */}
                    <path d="M150 260 Q200 250 300 258 Q380 252 450 260 L450 280 Q380 272 300 278 Q200 270 150 280Z" fill="url(#waterGrad)" />

                    {/* Trees (simplified triangles) */}
                    <polygon points="380,210 388,190 396,210" fill="#22c55e" opacity="0.2" />
                    <polygon points="400,215 407,198 414,215" fill="#22c55e" opacity="0.15" />

                    {/* Sun */}
                    <circle cx="420" cy="60" r="30" fill="#fbbf24" opacity="0.15" />
                    <circle cx="420" cy="60" r="18" fill="#fbbf24" opacity="0.25" />
                  </svg>
                </div>
              </div>
            </Reveal>

            <Reveal delay={200}>
              <div>
                <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 bg-brand-50 px-4 py-1.5 rounded-full mb-5">Source of Origin</span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-silver-950 tracking-tight">
                  The Hilly Edge
                </h2>
                <p className="mt-6 text-silver-600 leading-relaxed text-[15px]">
                  Our water originates from pristine hillside sources — naturally filtered through layers of rock and soil. This gives Arohix a distinct mineral profile and taste that sets it apart from standard borewell-sourced water.
                </p>
                <p className="mt-4 text-silver-600 leading-relaxed text-[15px]">
                  The natural pre-filtration, combined with our 7-stage industrial process, results in water that is not just safe — but genuinely premium in every sense.
                </p>
                <div className="mt-8 grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-white to-silver-50 rounded-2xl p-6 border border-silver-100/80 hover:shadow-card hover:-translate-y-0.5 transition-all duration-300">
                    <div className="text-3xl font-extrabold text-gradient">pH 7.2+</div>
                    <div className="text-xs text-silver-500 mt-1.5 font-medium">Naturally Alkaline</div>
                  </div>
                  <div className="bg-gradient-to-br from-white to-silver-50 rounded-2xl p-6 border border-silver-100/80 hover:shadow-card hover:-translate-y-0.5 transition-all duration-300">
                    <div className="text-3xl font-extrabold text-gradient">TDS 80</div>
                    <div className="text-xs text-silver-500 mt-1.5 font-medium">Optimal Mineral Balance</div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ VALUES ═══ */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-silver-50/50 to-white relative">
        <div className="absolute inset-0 dot-grid opacity-[0.2]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-16">
              <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 bg-brand-50 px-4 py-1.5 rounded-full mb-4">What Drives Us</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-silver-950 tracking-tight">
                Built on Four Pillars
              </h2>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Eye className="w-6 h-6 text-amber-500" />, title: 'Transparency', description: 'Open about our process, certifications, and sourcing. Nothing hidden.', bg: 'from-amber-50 to-orange-50' },
              { icon: <Zap className="w-6 h-6 text-brand-500" />, title: 'Innovation', description: '7-stage multi-barrier process with cutting-edge automation technology.', bg: 'from-brand-50 to-blue-50' },
              { icon: <Globe className="w-6 h-6 text-emerald-500" />, title: 'Sustainability', description: 'Eco-conscious sourcing and efficient production with minimal waste.', bg: 'from-emerald-50 to-teal-50' },
              { icon: <ShieldCheck className="w-6 h-6 text-violet-500" />, title: 'Trust', description: 'ISO, FSSAI, BIS certified. Every batch tested. Every bottle traceable.', bg: 'from-violet-50 to-purple-50' },
            ].map((value, i) => (
              <Reveal key={value.title} delay={i * 100}>
                <div className="group bg-white border border-silver-100 rounded-2xl p-7 hover:shadow-card-hover hover:border-silver-200/80 hover:-translate-y-1 transition-all duration-500 gradient-border h-full">
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${value.bg} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    {value.icon}
                  </div>
                  <h3 className="text-base font-bold text-silver-900 mb-2">{value.title}</h3>
                  <p className="text-sm text-silver-500 leading-relaxed">{value.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ DIRECTORS ═══ */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-16">
              <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 bg-brand-50 px-4 py-1.5 rounded-full mb-4">Leadership</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-silver-950 tracking-tight">
                Meet Our <span className="text-gradient">Directors</span>
              </h2>
              <p className="mt-5 text-silver-500 max-w-lg mx-auto text-lg">
                The visionary leaders driving Arohix Water's mission of transparent, science-first hydration.
              </p>
            </div>
          </Reveal>

          <div className="flex flex-col sm:flex-row justify-center gap-10 lg:gap-16">
            <Reveal delay={0}>
              <div className="group flex flex-col items-center text-center">
                <div className="relative w-[480px] h-96 rounded-3xl overflow-hidden border border-silver-100/80 shadow-card mb-6 group-hover:shadow-card-hover group-hover:-translate-y-1 transition-all duration-500">
                  <img
                    src="/assets/Director-2.jpeg"
                    alt="Director"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-900/20 to-transparent" />
                </div>
                <h3 className="text-lg font-bold text-silver-900">Mr. Avin Tapkeer</h3>
                <p className="text-sm text-brand-600 font-semibold mt-1">Director</p>
                <p className="text-xs text-silver-500 mt-3 max-w-[220px] leading-relaxed">Nathkrupa Energy</p>
              </div>
            </Reveal>

            <Reveal delay={150}>
              <div className="group flex flex-col items-center text-center">
                <div className="relative w-[480px] h-96 rounded-3xl overflow-hidden border border-silver-100/80 shadow-card mb-6 group-hover:shadow-card-hover group-hover:-translate-y-1 transition-all duration-500">
                  <img
                    src="/assets/Director-1.jpeg"
                    alt="Director"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-900/20 to-transparent" />
                </div>
                <h3 className="text-lg font-bold text-silver-900">Mrs. Arohi Avin Tapkeer</h3>
                <p className="text-sm text-brand-600 font-semibold mt-1">Director</p>
                <p className="text-xs text-silver-500 mt-3 max-w-[220px] leading-relaxed">Nathkrupa Energy</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="relative py-16 lg:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-700 via-brand-600 to-brand-800" />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/5 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-white/[0.03] rounded-full" />
        </div>

        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
              Ready to Experience the Difference?
            </h2>
            <p className="mt-6 text-lg text-brand-100/70 max-w-xl mx-auto leading-relaxed">
              Join the growing network of businesses and individuals who trust Arohix for premium hydration.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <button onClick={openQuote} className="shimmer-btn group inline-flex items-center gap-2.5 bg-white text-brand-700 px-8 py-4 rounded-full text-sm font-bold hover:bg-brand-50 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]">
                Get in Touch
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
              <Link to="/science" className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white transition-colors duration-300">
                Our Science <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

    </main>
  );
};

export default About;
